import React from "react";
import { Row, Col, Switch, Button, Select, Dropdown } from "antd";
import { usePlayerData } from "../../hooks/usePlayerData";
import { useHeadshot } from "@hooks/useHeadshot";
import { PlayerInfo } from "./PlayerInfo";
import { useDispatch } from "react-redux";
import { trainingAPI } from "../../api/TrainingAPI";
import { StatTileV2 } from "../StatTileV2/StatTileV2.jsx";
import { CompactStatTile } from "../StatTileV2/CompactStatTile.jsx"
import { useState, useEffect, useMemo, useRef } from "react";
import { useDataAccess } from "@components/PermissionedComponents";
import { withLockedStreamGroups } from "@components/PermissionedComponents/lockedStreamGroups";
import { useSelector } from "react-redux";
import { setPlayerProfileStatDefaults, setTeamPresets } from "../../store/actions/team.actions.js";
import { getDropdownKey, getMetricDropdownLabel, getMetricDropdownList,
	MAX_VELO_METRICS, TOTAL_DISTANCE_METRICS, findNearestNumber, get_metric_info_from_identifier } from "../../Utils/chart_utils";
import BarChartHighcharts from "../Charts/BarChart.jsx";
import { StatChartV2 } from "../StatChartV2/StatChartV2.jsx";
import {
	getRestAndTravelDaysInChart,
  } from "../Team/TeamAvgCharts";
import { BodyMapView } from "../BodyMapView/BodyMapView";
import { addRestAndTravelDates } from '../Subjective/SubjectiveCharts.jsx'
import { mapDataForHighcharts, mapDataForMaxVeloChart } from "../../mappers/model/BarChartMapper.js";
import { stackedBarChartDtoToModel } from "../../mappers/model/StackedBarChartMapper.js";
import {mapDataForHighchartsGroupedBar} from "../../mappers/model/GroupedBarChartMapper.js";
import { mapDataForBarAndBubbleChart } from "../../mappers/model/BarAndBubble.js";
import { useMetricDropdowns } from "../../hooks/useDropdownInitialization.js"
import { buildWellnessScoreStack, WELLNESS_SCORE_KEY } from "@utils/wellness_utils";
import { useAuth0 } from "@auth0/auth0-react";
import { PresetSelection, PresetType } from "../PresetSelection.tsx";
import { MultiMetricChart } from "../MultMetricChart/MultiMetricChart.jsx";
import { audioMessage as message } from "../Common/Notifications/audioMessage";
import { DataColorUtil } from "../../Utils/color_utils";
import OptionalFeaturesForCharts from "../Charts/OptionalFeaturesForCharts.jsx";
import CognitionBellCurveChart from "@components/Cognition/CognitionBellCurveChart";



const PlayerProfile = ({ 
	showACWR,
	showTravel,
	showRest,
	handleProfileStatChange,
	onOpenNotes
}) => {
	const {
		player,
		playerObject,
		playerName,
		useOverlay,
		team_logo,
	} = usePlayerData();
	const headshotSrc = useHeadshot(playerObject);

	const dispatch = useDispatch();
	const PRESET_TYPE = PresetType.Profile;
	const DEFAULT_PRESET = 'Default'

	const [pending, setPending] = useState(false);
	const [option, setOption] = useState("latest_vs_prev");
	const [currentSelectedTilePreset, setCurrentSelectedTilePreset] = useState(DEFAULT_PRESET)

	const {
		games,
		playerProfileStatDefaults,
		misc: { comparison_types },
		dropdowns: {player: playerDropdowns},
		info: {display_name: teamName, team_colors: teamColors, restAndTravel, custom_charts: customChartData,
			acwr_threshold_config: acwrThresholdConfig, metric: teamMetric
		},
		all_metric_types,
		presets,
		color_strategies: colorStrategies
	} = useSelector(({ team }) => team);

	// The dropdowns endpoint omits streams the user cannot read, so locked ones are
	// put back as a heading with a single unclickable row.
	const { canAccess } = useDataAccess();
	const profileDropdowns = useMemo(
		() => withLockedStreamGroups(playerDropdowns, canAccess, all_metric_types),
		[playerDropdowns, canAccess, all_metric_types]
	);

	const {
		user,
	  } = useAuth0();

	const {
		trainings,
		testingData,
		fpData,
		fpmsData,
		filter: { showGameDays, endDate, startDate, player: filterPlayer, session, showWeekly, showPredictions },
	} = useSelector((store) => store['player']);

	const [metricKeys, setMetricKeys] = useState(new Array(8).fill(""));
	const [chartMetricKeys, setChartMetricKeys] = useState(new Array(8).fill(""));
	const [profileCharts, setProfileCharts] = useState(null);
	const [chartElements, setChartElements] = useState(null);
	const [unsavedChanges, setUnsavedChanges] = useState(false)

	const [dataColorUtil, setDataColorUtil] = useState(null);

	useMetricDropdowns(['testing', 'forceplate', 'gps', 'wellness', 'nutrition', "cognition"]);

	// A tile edit already points its own chart at the new metric and stat, so the seeding
	// pass below must not run for it and put the saved default back.
	const isTileEdit = useRef(false);

	useEffect(() => {
		if (isTileEdit.current) {
			isTileEdit.current = false;
			return;
		}
		const chartData = new Array(8).fill("")
		metricKeys.forEach((key, idx) => {
			chartData[idx] =
			  playerProfileStatDefaults?.[1]?.[idx] ??
			  playerProfileStatDefaults?.[0]?.[idx] ??
			  key;
		  });
		setChartMetricKeys(chartData);
	}, [currentSelectedTilePreset, metricKeys]);

	useEffect(() => {
		if (trainings?.colors && trainings?.metrics?.length && colorStrategies?.length) {
			setDataColorUtil(new DataColorUtil({
				teamName,
				gpsBaselines: trainings.colors, // using colors as baselines
				teamGpsMetrics: trainings.metrics,
				colorStrategies
			}));
		} else {
			setDataColorUtil(null);
		}
	}, [trainings?.colors, trainings?.metrics, teamName, colorStrategies]);

	const enableDrillDown = useMemo(() => {
		return (showGameDays || showWeekly || showPredictions) ? false : true
	}, [showGameDays, showWeekly, showPredictions])

	const [stat, setStat] = useState("Avg");

	const handleChangeMetricKeys = (index) => (value) => {
		const newMetricKeys = [...metricKeys];
		newMetricKeys[index] = value;
		isTileEdit.current = true;
		setMetricKeys(newMetricKeys);
		handleChartDDClick(index)(value);

		setUnsavedChanges(true)
	};

	const saveTiles = async () => {
		setPending(true);
		try {
			const newMetricTiles = [metricKeys, chartMetricKeys];

			if (currentSelectedTilePreset === DEFAULT_PRESET) {
				await trainingAPI.updateDefaultStats(newMetricTiles, "profile");
				dispatch(setPlayerProfileStatDefaults(newMetricTiles));
			} else {
				const presetModified = presets?.find((preset) => preset.name === currentSelectedTilePreset)
				await trainingAPI.updatePreset({
					name: currentSelectedTilePreset, type: PRESET_TYPE, config: newMetricTiles[0], preset_id: presetModified.preset_id
				});
				presetModified.config = newMetricTiles[0]
				dispatch(setTeamPresets(presets))
			}
			message.success("Saved tiles");
		} catch (e) {
			message.error("Error saving tiles");
		}
		setPending(false);
	};

	const handleChartDDClick = (index) => (value) => {
		const newMetricKeys = [...chartMetricKeys];
		if (typeof value === 'object' ) {
			newMetricKeys[index] = value?.key;
		} else {
			newMetricKeys[index] = value;
		}
		setChartMetricKeys(newMetricKeys);
	};

	const gameDates = useMemo(
		() =>
			trainings?.data?.reduce(
				(cur, data) => ({ ...cur, [data.date]: data.opponent }),
				{}
			),
		[trainings]
	);

	const filteredData = useMemo(
		() =>
			showGameDays && trainings?.data
				? {
						...trainings,
						data: trainings?.data?.filter((data) => {
							if (data.game_id) gameDates[data.date] = true;
							return data.game_id;
						}),
						wellness: Object.keys(trainings?.wellness || {}).reduce(
							(cur, key) => ({
								...cur,
								[key]: trainings?.wellness?.[key]?.filter(
									(data) => gameDates[data.date]
								),
							}),
							{}
						),
				  }
				: trainings,
		[trainings, gameDates, showGameDays]
	);

	const restAndTravelDates = useMemo(
		() =>
		  getRestAndTravelDaysInChart(
			startDate,
			endDate,
			Object.values(trainings.wellness || {})?.[0],
			restAndTravel,
			showRest,
			showTravel
		  ),
		[startDate, endDate, restAndTravel, showRest, trainings, showTravel]
	);

	const wellnessScoreStack = useMemo(
		() =>
			buildWellnessScoreStack(
				filteredData?.wellness,
				filteredData?.wellness_metrics
			),
		[filteredData]
	);

	const getMappingFunction = (dataIn, metric, chartType = null, showTitle = true) => {
		let customChart = chartType ? metric : null
		if(metric?.customChartData) {
			customChart = metric?.customChartData
			chartType = metric?.customChartData?.type
		}

		// check if the team has custom chart available for this metric
		const customCharts = customChartData?.chart_types || []
		const chart = customCharts.find( (chart) => metric?.key === chart?.bar1_metric || metric?.key === chart?.bar_metric || metric?.key === chart?.stack_type)
		if(chart) {
			customChart = chart
			chartType = chart.type
		}
	
		switch (chartType) {
			case 'bar_and_bubble_chart': {
				return mapDataForBarAndBubbleChart({
					dataIn,
					mainSeriesMetric: dataIn.metrics.find( (metric) => metric.key === customChart?.bar_metric),
					secondarySeriesMetric: dataIn.metrics.find( (metric) => metric.key === customChart?.bubble_metric),
					showACWR,
					showRest,
					showTravel,
					restAndTravel,
					startDate,
					endDate,
					teamName,
					showPredictions,
					games,
					teamColors: teamColors.all,
					showAvgPlotline: true,
					acwrThresholdConfig,
					showTitle,
					customTitle: customChart?.title,
					dataColorUtil,
					enableDrillDown: enableDrillDown,
					selectedPlayerId: player
				});
			}
			case 'grouped_bar_chart': {
				return mapDataForHighchartsGroupedBar({
					dataIn,
					games,
					metrics: dataIn.metrics.filter( (metric) => metric.key === customChart?.bar1_metric || metric.key === customChart?.bar2_metric),
					teamColors: teamColors.all,
					isComparison: false,
					showACWR,
					showRest,
					showTravel,
					restAndTravel,
					startDate,
					endDate,
					showPredictions,
					isTeamChart: false,
					isMetricComparisonChart: true,
					title: customChart.title,
					showTitle,
					dataColorUtil,
					enableDrillDown: enableDrillDown,
					selectedPlayerId: player
				});
			}
			case 'stacked_bar_chart': {
				return stackedBarChartDtoToModel({
				  data: dataIn?.data,
				  showRest,
				  showTravel,
				  restAndTravel,
				  startDate,
				  endDate,
				  chartType: customChart?.stack_type,
				  showPredictions,
				  colors_100: dataIn?.colors,
				  games,
				  teamName,
				  showACWR: customChart?.stack_type === 'distance' ? false : true,
				  customChartConfig: customChart,
				  metrics: dataIn?.metrics,
				  acwrThresholdConfig,
				  showTitle,
				  enableDrillDown: enableDrillDown,
				  selectedPlayerId: player
				});
			}
			default: {
				// custom chart type either not provided or not recognized.
				// Will get handled by the code below
			}
		}
	
		if(MAX_VELO_METRICS.concat(TOTAL_DISTANCE_METRICS).includes(metric?.key)) {
		  return mapDataForMaxVeloChart({
			dataIn,
			metric,
			showRest,
			showTravel,
			restAndTravel,
			startDate,
			endDate,
			showACWR,
			games,
			teamName: teamName,
			showPredictions,
			teamColors: teamColors?.all,
			acwrThresholdConfig,
			showTitle,
			dataColorUtil,
			enableDrillDown: enableDrillDown,
			selectedPlayerId: player
		  })
		}
		 
		return mapDataForHighcharts({
			dataIn,
			metric,
			showACWR,
			showRest,
			showTravel,
			restAndTravel,
			startDate,
			endDate,
			games,
			teamName: teamName,
			showPredictions,
			teamColors: teamColors?.all,
			teamMetric,
			acwrThresholdConfig,
			showTitle,
			dataColorUtil,
			enableDrillDown: enableDrillDown,
			selectedPlayerId: player
		});
}

	const getAnswerByValue = (key, value) => {
		const metric = filteredData.wellness_metrics.find((m) => m.key === key);
		if (metric && metric.answers) {
		  const [answer, index] = findNearestNumber(metric.answers, value);
		  if (answer) {
			return [answer.answer, index];
		  }
		}
		return [null];
	  };

	function findMaxValueObject(array, key) {
		if (array.length === 0) {
		  return null; // Handle empty array
		}
	
		let maxValueObject = array[0];
		for (let i = 1; i < array.length; i++) {
		  if (array[i][key] - maxValueObject[key] > 0) {
			maxValueObject = array[i];
		  }
		}
		return maxValueObject[key];
	  }


	const getGPSPlayerChart = (currChartMetricKey, trainings, metric) => {
		const custom_chart_obj = customChartData?.chart_types?.find(obj =>
			Object.values(obj).some(value => value === currChartMetricKey)
		)

		let chart_type = null;
		if (custom_chart_obj) {
			chart_type = custom_chart_obj?.type;

			const allBarMetrics = Object.entries(custom_chart_obj)
								.filter(([key]) => key.startsWith("bar") && key.endsWith("_metric"))
								.map(([, value]) => value);

			if (metric?.key !== allBarMetrics[0]) {
				metric = Object.values(all_metric_types?.gps).flat()?.find((m) => 
					m.key === allBarMetrics[0]
				);
			}
		}
		
		const mapping = getMappingFunction(trainings, metric, chart_type, false);
		
		return (
			<div className="example-table-graph">
				<OptionalFeaturesForCharts
					chartType={'bar'}
					showAvgPlotline={true}
					optionalFeatures={{ showTrendlines: "Trendline" }}
					optionsBuilder={(features) => {
						const opts = mapping; // mapping already accounts for features via getMappingFunction
						return {
							...opts,
							chart: {
								...(opts?.chart || {}),
								height: '100%'
							}
						};
					}}
				/>
			</div>
		)
	}

	const getWellnessScoreStackChart = () => {
		const { rows, labels } = wellnessScoreStack;
		if (!rows.length || !labels.length) return null;

		const options = stackedBarChartDtoToModel({
			data: addRestAndTravelDates(rows, [WELLNESS_SCORE_KEY], restAndTravelDates),
			wellnessLabels: labels,
			chartType: "wellness",
			games,
			teamName,
			showACWR: false,
			acwrThresholdConfig,
			showTitle: false,
			enableDrillDown,
			session,
			selectedPlayerId: player,
		});

		return (
			<BarChartHighcharts
				className="wellness-bar-charts"
				options={{ ...options, chart: { ...options?.chart, height: "100%" } }}
			/>
		);
	}


	useEffect(() => {
		if (chartMetricKeys === undefined || chartMetricKeys === null || chartMetricKeys.length === 0 ||
			chartMetricKeys.every(key => key === "" || key === undefined || key === null)) return;

		const chart_columns = new Array(8).fill(0).map((_, i) => {

			const {metric_id, metric_stat} = get_metric_info_from_identifier(chartMetricKeys[i]);
			const metric = Object.values(all_metric_types)
						.flat()
						.find((metric) => getDropdownKey(metric, "metricId") === metric_id);

			const title = !metric ? "Choose Metric" : getMetricDropdownLabel(metric);
		
			const currChartMetricIdStr = metric_id;
			const currChartMetricKey = metric?.key;

			if (currChartMetricIdStr === undefined || currChartMetricIdStr === null || currChartMetricIdStr === '') 
				return (<></>)

			return (
				<Col
					key={`chart-col-${i}`}
					span={12}
					style={{
						display: 'flex',
						justifyContent: 'center'
					}
				}>
					<div className="player-profile-chart">
						<div>
							<Dropdown
								menu={{
									items: profileDropdowns || [],
									onClick: handleChartDDClick(i),
								}}
								trigger={["hover"]}
								style={{
									width: '100%',
								}}
							>
								<Button
									style={{
									fontSize: 16,
									height: "32px",
									whiteSpace: "nowrap",
									maxWidth: 310,
									textAlign: "center",
									align: 'center',
									overflow: "hidden",
									border: '1px solid #cccccc'
									}}
									size="small"
									type="text"
								>
									<div className={title.length > 33 ? "chartLabelContainer": 'none'}>
										<span><h5 style={{ margin: 0 }}>{title}</h5></span>
									</div>

								</Button>
							</Dropdown>
						</div>
						<div style={{ height: '100%'}}> 
							{
								(currChartMetricIdStr?.startsWith("gps")) ? (
									getGPSPlayerChart(currChartMetricKey, trainings, metric)
								)
									:
									(
										(currChartMetricIdStr?.startsWith("nutrition")) ? (
											<MultiMetricChart
												values={[currChartMetricIdStr]}
												metricType="nutrition"
												startDate={startDate}
												endDate={endDate}
												playerId={player}
												stat={stat}
												position={[]}
												showTitle={false}
												showDataLabelToggle={false}
												chartIndex={i}
												pageType="profile"
											/>
								)
								:
								(
									(currChartMetricIdStr?.startsWith("forceplate")) ? (
										<OptionalFeaturesForCharts
											chartType={'stat'}
											optionalFeatures={{ showTrendlines: "Trendline" }}
											value={currChartMetricIdStr}
											metricType="forceplate"
											startDate={startDate}
											endDate={endDate}
											playerId={player}
											stat={metric_stat || stat}
											position={[]}
											colors={teamColors.all}
											player_group=""
											sessions={session}
											showTitle={false}
											enableDrillDown={enableDrillDown}
											dataColorUtil={dataColorUtil}
										/>
									)
									:
									(
										(currChartMetricIdStr?.startsWith("testing")) ? (
											<OptionalFeaturesForCharts
												chartType={'stat'}
												optionalFeatures={{ showTrendlines: "Trendline" }}
												value={currChartMetricIdStr}
												metricType="testing"
												startDate={startDate}
												endDate={endDate}
												playerId={player}
												stat={metric_stat || stat}
												type={"player"}
												position={[]}
												colors={teamColors.all}
												player_group={""}
												sessions={session}
												showTitle={false}
												dataColorUtil={dataColorUtil}
												enableDrillDown={enableDrillDown}
											/>
										)
										:
										(
											(currChartMetricIdStr?.startsWith("cognition")) ? (
												<CognitionBellCurveChart
													statMetrics={[currChartMetricIdStr]}
													startDate={startDate}
													endDate={endDate}
													playerId={player}
													playerName={playerName}
													session={session}
												/>
											)
											:
											(
											currChartMetricKey === WELLNESS_SCORE_KEY ? (
												getWellnessScoreStackChart()
											) : (
											filteredData?.wellness_metrics
												.filter(x => x.answer_type !== 'string')
												.filter((metric) => metric.key !== "wellness_score")
												.map((metric, i) => {
													if (metric.key === currChartMetricKey) {
														const chart_data = addRestAndTravelDates(
															filteredData["wellness"][metric.key].map((dt) => {
															const [answerTxt, index] =
																metric.answer_type?.toLowerCase() === "float" ||
																metric.answer_type?.toLowerCase() === "int"
																? [dt[metric.key], -1]
																: getAnswerByValue(
																	metric.key,
																	Math.round(dt[metric.key])
																	);
															const gameDay = games.find(
																(game) => game.date === dt.date
															);
															return {
																...dt,
																answerTxt,
																answerIndex: index,
																opponent: gameDay?.opponent,
															};
															}),
															[metric.key, "answerIndex"],
															restAndTravelDates
														);
														const maxValue = Math.round(
															findMaxValueObject(chart_data, metric?.key) || 0
														);
											
														const hasBodyMap = filteredData.bodymap_data?.hasOwnProperty(
															metric.key
														);
											
														return (
															hasBodyMap ? (
																<div className="profile-bodymap-stack">
																<BarChartHighcharts
																	className="wellness-bar-charts"
																	options={{
																	...mapDataForHighcharts({
																		dataIn: {data: chart_data},
																		metric,
																		teamName,
																		games,
																		teamColors,
																		singleAxis: true,
																		isSubjectiveMetric: true,
																		selectedPlayerId: player,
																		teamMetric,
																		acwrThresholdConfig,
																		showTitle: false
																	}),
																	chart:{
																		...mapDataForHighcharts({
																		dataIn: {data: chart_data},
																		metric,
																		teamName,
																		games,
																		teamColors,
																		singleAxis: true,
																		isSubjectiveMetric: true,
																		selectedPlayerId: player,
																		teamMetric,
																		acwrThresholdConfig,
																		showTitle: false
																		})?.chart,
																		height: '100%'
																	}
																	}}>
																</BarChartHighcharts>
																<div className="profile-bodymap-map">
																	<BodyMapView
																	playersPage={true}
																	title={`${metric.short} Map`}
																	data={filteredData.bodymap_data[currChartMetricKey]}
																	question_key={metric.key}
																	/>
																</div>
																</div>
															) : (
																<BarChartHighcharts 
																className="wellness-bar-charts"
																options={{
																	...mapDataForHighcharts({
																	dataIn: {data: chart_data},
																	metric,
																	teamName,
																	teamColors,
																	games,
																	singleAxis: true,
																	isSubjectiveMetric: true,
																	selectedPlayerId: player,
																	teamMetric,
																	acwrThresholdConfig,
																	showTitle: false
																	}),
																	chart:{
																	...mapDataForHighcharts({
																		dataIn: {data: chart_data},
																		metric,
																		teamName,
																		teamColors,
																		games,
																		singleAxis: true,
																		isSubjectiveMetric: true,
																		selectedPlayerId: player,
																		teamMetric,
																		acwrThresholdConfig,
																		showTitle: false
																	})?.chart,
																	height: '100%'
																	}
																}}></BarChartHighcharts>
															)
														);
													} else {
														return (<></>)
													}
												
												})
											)
										)
									)
								)
							)
								)}

							
						</div>
					</div>
				</Col>
			)
		});
		setProfileCharts(chart_columns);
	}, [chartMetricKeys, trainings, fpData, fpmsData, testingData, stat])

	return (
		<>
			<Row
				justify="center"
				align="middle"
				style={{ width: "100%", marginBottom: "1rem" }}
			>
				<Col span={24}>
					<div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "1rem" }}>
						<PlayerInfo
							playerName={playerName}
							playerData={playerObject}
							headshot={headshotSrc}
							useOverlay={useOverlay}
							team_logo={team_logo}
							onOpenNotes={onOpenNotes}
						/>
					</div>
				</Col>
			</Row>
			<Row>
				<div style={{marginTop: "1rem" }} className="exportable tab-profile profile-stats profile-stats-toggles flex gap-16 mb-16 items-center w-full justify-center">
					<Row
					gutter={16}
					style={{ width: "80%", marginBottom: 16, justifyContent: 'center', gap: '10px' }}>
						<PresetSelection 
							presets={presets} 
							user={user} 
							presetType={PRESET_TYPE} 
							statDefaults={playerProfileStatDefaults[0]}
							currStatMetrics={metricKeys}
							setStatMetrics={setMetricKeys} 
							currentSelectedTilePreset={currentSelectedTilePreset}
							setCurrentSelectedTilePreset={setCurrentSelectedTilePreset}
							comparisonTypes={comparison_types}
							comparisonOption={option}
							setComparisonOption={setOption}
							handleSave={saveTiles}
							warnUnsavedChanges={unsavedChanges}
							setWarnUnsavedChages={setUnsavedChanges}
							/>
					</Row>
				</div>
			</Row>
			<Row>
				<Col span={24} style={{
						display: 'flex',
						justifyContent: 'center'
					}}>
					<div
						style={{
							display: "grid",
							gridTemplateColumns: "repeat(4, 1fr)", 
							columnGap: '1rem',
							justifyContent: "center",
							justifyItems: "center",
							alignItems: "start", 
							width: "70%",
						}}
					>
						{new Array(8).fill(0).map((_, i) => (
							<div
								key={i}
								style={{
									width: "100%",
									height: "100%", 
									display: "flex",
									justifyContent: "center",
									marginTop: i >= 4 ? '15px' : 0
								}}
							>
								<CompactStatTile
									dropdowns={profileDropdowns}
									metric_identifier_with_stat={metricKeys[i]}
									onChange={handleChangeMetricKeys(i)}
									option={option}
									endDate={endDate}
									startDate={startDate}
									playerId={player}
									isCompact={true}
									comparison={option}
									type="player"
								 />
							</div>
						))}
					</div>
				</Col>
			</Row>
			<Row>
				{
					profileCharts
				}
			</Row>
			<div style={{marginTop: "10vh"}} />
		</>
	);
};

export default React.memo(PlayerProfile);